import React from 'react';
import { motion } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Calendar, Clock } from 'lucide-react';
import { getSignedMediaUrl } from '@/lib/utils';
import { TransferStatusBadge } from './TransferStatusBadge';
import type { TransferBookingItem, TransferDisplayInfo } from '@/features/booking/types/transferTypes';

interface TransferCardProps {
    item: TransferBookingItem;
    displayInfo: TransferDisplayInfo;
    expiresAtFormatted?: string;
    dateFormatted: string;
    timeFormatted: string;
    isCopied: boolean;
    onCopyLink: () => void;
}

export const TransferCard: React.FC<TransferCardProps> = ({
    item,
    displayInfo,
    expiresAtFormatted,
    dateFormatted,
    timeFormatted,
    isCopied,
    onCopyLink,
}) => {
    const navigate = useNavigate();

    const handleViewTransfer = () => {
        navigate(`/transfer-booking/t/${item.transferId}`);
    };

    return (
        <motion.div
            className="bg-gray-900 border border-gray-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
            onClick={handleViewTransfer}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{
                y: -4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
                transition: { type: "spring", stiffness: 300, damping: 30 }
            }}
            transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                mass: 0.8
            }}
        >
            {/* Card with Background Image */}
            <div
                className="relative h-32 sm:h-48 lg:h-64 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: item.eventImageId
                        ? `url(${getSignedMediaUrl(item.eventImageId, 'image').url})`
                        : undefined
                }}
            >
                {!item.eventImageId && (
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-900/50 to-gray-900 pointer-events-none" />
                )}
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-black/60 pointer-events-none" />

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-3 sm:p-6 lg:p-8">
                    {/* Top Section: Event Info */}
                    <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-sm sm:text-lg lg:text-2xl font-serif font-bold text-white mb-1 sm:mb-2 line-clamp-2">
                            {item.eventName}
                        </h3>
                        {item.eventDescription && (
                            <p className="text-gray-300 text-xs sm:text-sm line-clamp-1 sm:line-clamp-2">
                                {item.eventDescription}
                            </p>
                        )}
                        {/* Date & Time */}
                        <div className="flex items-center gap-2 sm:gap-4 text-white pt-1 sm:pt-2">
                            <span className="flex items-center gap-1 sm:gap-1.5">
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-xs sm:text-sm font-medium">{dateFormatted}</span>
                            </span>
                            <span className="flex items-center gap-1 sm:gap-1.5">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-xs sm:text-sm font-medium">{timeFormatted}</span>
                            </span>
                        </div>
                    </div>

                    {/* Bottom Section: Status Badge */}
                    <div className="space-y-1 sm:space-y-2">
                        <TransferStatusBadge
                            displayInfo={displayInfo}
                            expiresAtFormatted={expiresAtFormatted}
                        />
                    </div>
                </div>

                {/* Actions - positioned at bottom right */}
                <div className="absolute bottom-3 right-3 sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-8 flex items-center gap-3 z-20">
                    {/* Copy Link Button - Only for Pending transfers */}
                    {displayInfo.isPending && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onCopyLink(); }}
                            className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${isCopied
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                                }`}
                        >
                            {isCopied ? (
                                <>
                                    <Check className="w-4 h-4" />
                                    <span>Copied</span>
                                </>
                            ) : (
                                <>
                                    <Copy className="w-4 h-4" />
                                    <span>Copy Link</span>
                                </>
                            )}
                        </button>
                    )}

                </div>
            </div>
        </motion.div>
    );
};

export default TransferCard;
